import React, { useMemo } from 'react';

import { t } from '@sm/intl';
import { Typography } from '@wds/typography';

import { COMPARISON_PACKAGES } from '~app/pages/Pricing/lib/constants';
import { Packages, PackageDisplayNames } from '~shared/constants/pricing';
import { PackageData, PACKAGE_PRICE_TYPE } from '~shared/types/pricing/package';
import { PackageHeaderProps } from '~pricing/components/Summary/types';

import PackageByline from '~pricing/components/PackageHeader/PackageByline';
import PackageCta from '~pricing/components/PackageCta';
import packageDefs from '~pricing/lib/packages/packageDefs';
import PackagePrice from '~pricing/components/PackagePrice';
import Ribbon from '~pricing/components/Ribbon';
import usePackageHeaderStyles from '~pricing/components/PackageHeader/useStyles';

import {
  calculateSavingsPercent,
  formatMonthlyCostInLocaleCurrency,
  formatCostInLocaleCurrency,
  getCoreCostFromSkus,
} from '~app/pages/Pricing/lib/lib-helpers';

import usePriceDisplay from '~pricing/hooks/usePriceDisplay';

import COPY from './copy';
import { usePricingState } from '~shared/hooks/usePricingState';

export default function PackageHeader({
  packageName,
  comparisonSkuCosts,
  skuCosts,
  isSummaryPage = false,
  discountInfo,
}: PackageHeaderProps): JSX.Element {
  const { Header } = usePackageHeaderStyles({ isSummaryPage });
  const comparisonPackageName = COMPARISON_PACKAGES[packageName];

  const {
    state: {
      packages: { allPackages: packageData },
    },
  } = usePricingState();

  const { headerCost, headerDiscountedCost, headerByline, headerUnit } = usePriceDisplay(
    packageName,
    skuCosts,
    discountInfo
  );

  const coreSeatCost = useMemo(() => getCoreCostFromSkus(skuCosts), [skuCosts]);

  const comparisonCoreSeatCost = useMemo(
    () => comparisonSkuCosts && getCoreCostFromSkus(comparisonSkuCosts),
    [comparisonSkuCosts]
  );

  const comparisonFormattedPrice = useMemo(
    () => comparisonCoreSeatCost && formatCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonFormattedMonthlyPrice = useMemo(
    () => comparisonCoreSeatCost && formatMonthlyCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonPackage = useMemo(
    () =>
      comparisonPackageName
        ? packageData?.find((packageObj: PackageData) => packageObj.packageName === comparisonPackageName)
        : null,
    [comparisonPackageName, packageData]
  );

  const comparisonSeatCost = useMemo(
    () => (comparisonPackage ? getCoreCostFromSkus(comparisonPackage?.skuCost) : null),
    [comparisonPackage]
  );

  const savingsPercentage = useMemo(
    () => (coreSeatCost && comparisonSeatCost ? calculateSavingsPercent(comparisonSeatCost, coreSeatCost) : 0),
    [comparisonSeatCost, coreSeatCost]
  );

  const getRibbon = useMemo(() => {
    let ribbonText = packageDefs[packageName]?.highlighted ? t(COPY.BEST_VALUE) : null;

    if (savingsPercentage > 0) {
      ribbonText = t(COPY.SAVE_PERCENTAGE, { percentage: savingsPercentage });
    }

    if (ribbonText) {
      return <Ribbon packageName={packageName}>{ribbonText}</Ribbon>;
    }
    return null;
  }, [packageName, savingsPercentage]);

  return (
    <>
      {getRibbon}

      <header data-testid="PackageHeader__Header" className={Header}>
        <Typography component="h2" data-testid="PackageHeader__HeaderText" align="center">
          {t(PackageDisplayNames[packageName])}
        </Typography>
        {packageName !== Packages.Enterprise && (
          <PackagePrice
            packagePriceType={isSummaryPage ? PACKAGE_PRICE_TYPE.SUMMARY : PACKAGE_PRICE_TYPE.DETAIL}
            costUnit={headerUnit}
            formattedPrice={headerDiscountedCost ?? headerCost}
          />
        )}
        {![Packages.Enterprise, Packages.Basic].includes(packageName) && headerDiscountedCost && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            costUnit={headerUnit}
            formattedPrice={headerCost}
          />
        )}
        {comparisonFormattedPrice && !headerDiscountedCost && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            costUnit={headerUnit}
            formattedPrice={
              packageName === Packages.StandardMonthly ? comparisonFormattedPrice : comparisonFormattedMonthlyPrice
            }
          />
        )}

        <PackageByline byline={headerByline} />
        <PackageCta packageName={packageName} isSummaryPage={isSummaryPage} />
      </header>
    </>
  );
}
